// PlaceObjectTool — click to place a garden object (building or plant) on canvas
// Reads selected product from ui/state, creates object via gardenObjectState
// Supports single (place one, return to select) and continuous (keep placing) modes

import type { Tool, ToolContext } from '@/types/editor'
import type { ToolManager } from '../ToolManager'
import { gardenObjectState } from '@/state/GardenObjectState'
import { undoStack } from '@/editor/UndoStack'
import { getSelectedProduct, getSelectedProductLayer, setSelectedProduct, getViewer, getPlacementMode, stopContinuousPlacement, getToolMode } from '@/ui/state'
import { debug } from '@/utils/debug'

export class PlaceObjectTool implements Tool {
  readonly name = 'place-object'
  readonly cursor = 'crosshair'
  readonly hint = 'tool.hint.placeObject'

  private toolManager: ToolManager

  constructor(toolManager: ToolManager) {
    this.toolManager = toolManager
  }

  activate(): void {
    // Disable selection/hover during placement — just crosshair, no highlights
    const selMgr = getViewer()?.getScene()?.getSelectionManager()
    selMgr?.deselect()
    selMgr?.clearHover()
    selMgr?.setEnabled(false)
    debug.log('PlaceObjectTool', `Activated (mode: ${getPlacementMode()})`)
  }

  deactivate(): void {
    if (getToolMode() === 'pointer') {
      getViewer()?.getScene()?.getSelectionManager()?.setEnabled(true)
    }
    debug.log('PlaceObjectTool', 'Deactivated')
  }

  onMouseDown(ctx: ToolContext): void {
    if (ctx.event.button !== 0) return
    if (!ctx.editorPoint) return

    const product = getSelectedProduct()
    if (!product) {
      debug.warn('PlaceObjectTool', 'No product selected')
      return
    }

    // Determine object type from the layer that selected this product
    const layer = getSelectedProductLayer()
    const objectType = layer === 'architecture' ? 'building' : 'plant'

    // Ensure the product is cached in the viewer for renderer lookups
    getViewer()?.ensureProductCached(product)

    const { x, y } = ctx.editorPoint
    const width = product.width ?? 50
    const height = product.height ?? 50

    // Plants get a random initial rotation for natural variation
    const rotation = objectType === 'plant'
      ? Math.round(Math.random() * 360)
      : 0

    const id = gardenObjectState.addObject(
      objectType,
      product.id,
      x,
      y,
      width,
      height,
      {
        elevation: product.elevation ?? 0,
        productName: product.name,
        rotation,
      }
    )

    const snapshot = { ...gardenObjectState.getObjectById(id)! }

    undoStack.push({
      description: `Place ${objectType} ${id}`,
      undo: () => gardenObjectState.deleteObject(id),
      redo: () => gardenObjectState.restoreObject(snapshot),
    })

    debug.log('PlaceObjectTool', `Placed ${objectType}: ${id} (${product.name}) at (${x}, ${y})`)

    // In single mode, auto-select the placed object and return to select tool
    if (getPlacementMode() === 'single') {
      getViewer()?.selectAfterRender(id)
      setSelectedProduct(null)
      this.toolManager.setActiveTool('select')
    }

    ctx.event.preventDefault()
    ctx.event.stopPropagation()
  }

  onMouseMove(_ctx: ToolContext): void {
    // No preview needed for click-to-place
  }

  onMouseUp(_ctx: ToolContext): void {
    // Creation happens on mouseDown
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      stopContinuousPlacement()
      this.toolManager.setActiveTool('select')
    }
  }

  dispose(): void {
    // Nothing to clean up
  }
}
